// MARK://dhk8/pos/hooks/use-shortcuts
// Global keyboard shortcuts for the POS. Port of sc-restaurant's useGlobalShortcuts,
// polished into one consistent set:
//   '?'            → toggle the shortcuts cheat-sheet (returns the open state)
//   '/'            → focus the first search input on the page
//   'n'            → new order (go to the comanda)
//   'g' then <key> → navigate · f plano · m menú · c comanda · t tickets · b barra ·
//                    k cocina · j caja · r reportes
//   F2 / F9        → fire a custom event the active POS page can listen to (enviar/cobrar)
// All shortcuts are no-ops while focus is in an editable element, while the terminal is
// locked, or while a sheet/dialog is open (sheets trap Esc themselves). Esc-Esc terminal
// lock lives in useLockscreen().

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLockStore } from '../stores/lock-store.js'
import { usePinGateStore } from '../features/security/pin-gate-store.js'

const GOTO_WINDOW_MS = 900

/** `g`-then-key destinations. f/m/c/t cover the four operación screens; the rest are extras. */
const ROUTES: Record<string, string> = {
  f: '/floor',     // plano
  m: '/menu',      // menú
  c: '/order',     // comanda
  t: '/tickets',   // tickets
  b: '/pos/barra', // barra
  k: '/kds',       // cocina
  j: '/cashcut',   // caja (j: 'c' is taken by comanda)
  r: '/reports',   // reportes
  p: '/order',     // back-compat: 'g p' still goes to the comanda
}

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
}

export function useShortcuts() {
  const navigate = useNavigate()
  const locked = useLockStore((s) => s.locked)
  const pinGateOpen = usePinGateStore((s) => s.open)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const pendingGotoRef = useRef(0)

  const closeShortcuts = useCallback(() => setShortcutsOpen(false), [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (locked || pinGateOpen) return
      if (isEditable(e.target)) return

      // 'g' then a route key.
      if (e.key === 'g') {
        pendingGotoRef.current = Date.now()
        return
      }
      if (pendingGotoRef.current && Date.now() - pendingGotoRef.current <= GOTO_WINDOW_MS) {
        const dest = ROUTES[e.key.toLowerCase()]
        if (dest) {
          e.preventDefault()
          pendingGotoRef.current = 0
          navigate(dest)
          return
        }
        pendingGotoRef.current = 0
      }

      if (e.key === '?') {
        e.preventDefault()
        setShortcutsOpen((v) => !v)
        return
      }
      if (e.key === '/') {
        const search = document.querySelector<HTMLInputElement>('input[type="search"], input[data-global-search], input[type="text"][placeholder*="usca" i]')
        if (search) {
          e.preventDefault()
          search.focus()
        }
        return
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault()
        navigate('/order')
        return
      }
      if (e.key === 'F2' || e.key === 'F9') {
        window.dispatchEvent(new CustomEvent('dhk8:pos-shortcut', { detail: { key: e.key } }))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate, locked, pinGateOpen])

  return { shortcutsOpen, closeShortcuts }
}
